import { useTranslation } from 'react-i18next';
import { BankReconciliationImportResponse, BankReconciliationImportPart } from '../../api/client';

interface Props {
  data: BankReconciliationImportResponse;
  start: number;
  retrying: boolean;
  onPage: (start: number) => void;
  onRefresh: () => void;
  onOpen: (runId: string) => void;
  onRetry: (part: BankReconciliationImportPart) => void;
  onDelete: () => void;
}

export default function ImportProgress({ data, start, retrying, onPage, onRefresh, onOpen, onRetry, onDelete }: Props) {
  const { t } = useTranslation();
  const record = data.import_record;
  const running = ['admitting', 'queued', 'analyzing'].includes(record.status);
  return (
    <section aria-label={t('reconciliation.import.title')} className="space-y-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{record.original_filename}</h2>
        <div className="flex gap-2">
          <button type="button" onClick={onRefresh} className="rounded border px-3 py-1 text-sm">{t('common.refresh')}</button>
          {data.can_delete && <button type="button" onClick={onDelete}
            className="rounded border border-red-300 px-3 py-1 text-sm text-red-700">{t('reconciliation.deletion.action')}</button>}
        </div>
      </div>
      <p role="status" aria-live="polite" className="text-sm text-gray-700">
        {t('reconciliation.import.progress', {
          processed: record.processed_payments, total: record.total_payments,
          completed: record.completed_parts, parts: record.part_count, failed: record.failed_parts,
        })} · {t(`reconciliation.import.states.${record.status}`)}
      </p>
      <progress className="w-full" max={record.total_payments} value={record.processed_payments}
        aria-label={t('reconciliation.import.title')} />
      {running && <p className="text-sm text-gray-500">{t('reconciliation.import.background')}</p>}
      {record.error_message && <p role="alert" className="text-sm text-red-700">{record.error_message}</p>}
      <ul className="space-y-2">
        {data.parts.map((part) => (
          <li key={part.part_number} className="rounded-lg border p-3">
            <p className="text-sm font-medium">{t('reconciliation.import.part', {
              number: part.part_number, count: part.payment_count,
            })} · {t(`reconciliation.import.states.${part.status}`)}</p>
            {part.error_message && <p role="alert" className="mt-1 text-sm text-red-700">{part.error_message}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              {part.status === 'completed' && part.run_ids.map((runId, index) => (
                <button type="button" key={runId} onClick={() => onOpen(runId)}
                  className="rounded border border-primary-300 px-3 py-1 text-sm text-primary-700">
                  {t('reconciliation.import.review', { number: index + 1 })}
                </button>
              ))}
              {part.status === 'failed' && <button type="button" disabled={retrying || running}
                onClick={() => onRetry(part)} className="rounded border px-3 py-1 text-sm disabled:opacity-50">
                {t('reconciliation.import.retry')}
              </button>}
            </div>
          </li>
        ))}
      </ul>
      <nav aria-label={t('reconciliation.import.pages')} className="flex gap-2">
        <button type="button" disabled={start === 1} onClick={() => onPage(Math.max(1, start - 25))}
          className="rounded border px-3 py-1 text-sm disabled:opacity-50">{t('reconciliation.import.previous')}</button>
        <button type="button" disabled={!data.next_part} onClick={() => data.next_part && onPage(data.next_part)}
          className="rounded border px-3 py-1 text-sm disabled:opacity-50">{t('reconciliation.import.next')}</button>
      </nav>
    </section>
  );
}
